import React, { useState } from 'react';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  CloseButtonWrapper,
  ContentContainer,
  DialogWrapper,
  ModalContent,
  NavigationButtonWrapper,
  StyledDialogContent,
  StyledDialogTitle,
  StyledTextField,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { useContextStore } from '../../../../stores/contextStore';
import { cropConnectApiClient } from '../../../../services/cropConnectApiClient';
import { useTranslationContext } from '../../../../context/TranslationContext';

interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ open, onClose }) => {
  const { t } = useI18n();
  const { isTranslationMode } = useTranslationContext();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { updateUserDisplayName } = useContextStore();

  const validateName = (value: string) => {
    if (value.trim().length === 0) {
      setError(t(TranslationKeys.welcomemodal_input_error_empty) as string);
      return false;
    } else if (value.length > 50) {
      setError(t(TranslationKeys.welcomemodal_input_error_length) as string);
      return false;
    } else {
      setError('');
      return true;
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    validateName(value);
  };

  const handleClose = () => {
    cropConnectApiClient
      .initialLogin()
      .then(() => {})
      .catch(() => {});
    onClose();
  };

  const handleProceedClick = () => {
    if (validateName(name)) {
      setIsUpdating(true);
      cropConnectApiClient
        .initialLogin()
        .then(() => {})
        .catch(() => {});
      updateUserDisplayName(name)
        .then(() => {
          localStorage.setItem('userName', name);
          setIsUpdating(false);
        })
        .catch((e) => {
          console.error('Error updating user display name', e);
        })
        .finally(() => {
          onClose();
        });
    }
  };

  return (
    <DialogWrapper open={open} onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <CloseButtonWrapper>
          <IconButton className="modal-close-button" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </CloseButtonWrapper>
        <ModalContent>
          <ContentContainer>
            <StyledDialogTitle>
              {t(TranslationKeys.welcomemodal_title)}
            </StyledDialogTitle>
            <StyledDialogContent>
              <p className="paragraph-large">
                {t(TranslationKeys.welcomemodal_paragraph_large)}
              </p>
              <p className="paragraph-small">
                {t(TranslationKeys.welcomemodal_paragraph_small1)}
              </p>
              <p className="paragraph-small">
                {t(TranslationKeys.welcomemodal_paragraph_small2)}
              </p>
              <p className="input-label">
                {t(TranslationKeys.welcomemodal_input_title)}
              </p>

              {isTranslationMode ? (
                <div
                  style={{
                    border: '1px solid #d3d3d3',
                    padding: '12px',
                    borderRadius: '4px',
                    color: 'red',
                    minHeight: '80px',
                    width: '300px',
                  }}
                >
                  {t(TranslationKeys.welcomemodal_input_placeholder)}
                </div>
              ) : (
                <>
                  <StyledTextField
                    fullWidth
                    placeholder={
                      t(
                        TranslationKeys.welcomemodal_input_placeholder
                      ) as string
                    }
                    value={name}
                    onChange={handleNameChange}
                    error={Boolean(error)}
                    maxLength={50}
                    style={{ height: '40px' }}
                  />
                  {error && (
                    <span style={{ color: 'red', fontSize: '12px' }}>
                      {error}
                    </span>
                  )}
                </>
              )}

              <NavigationButtonWrapper
                style={{ flexDirection: isTranslationMode ? 'column' : 'row' }}
              >
                <ActionButton
                  variant="interaction-button"
                  buttonText={t(TranslationKeys.welcomemodal_button_skip)}
                  onClickAction={handleClose}
                  disabled={isUpdating}
                />
                <ActionButton
                  variant="accept-blue"
                  buttonText={t(TranslationKeys.welcomemodal_button_proceed)}
                  onClickAction={handleProceedClick}
                  disabled={
                    !isTranslationMode &&
                    (isUpdating || Boolean(error) || name.trim().length === 0)
                  }
                />
              </NavigationButtonWrapper>
            </StyledDialogContent>
          </ContentContainer>
        </ModalContent>
      </div>
    </DialogWrapper>
  );
};

export default WelcomeModal;
